import { ContentService } from '@blk/cwp-ng-framework';
import { AbstractRenderer, Alignment, CellRenderer, ColumnComposition, ColumnCompositions, MultipleLineTextRenderer, RowPanel } from '@blk/simba';
import { CellData, CellUtil, ColumnConfig, ColumnText, StaticValueExtractor, TabData, TableData } from '@ddt-web/data-service';

export class EsgHoldingRankingsUtils {
	private static readonly excludedMultiPortfolioColumns = ['portfolio', 'weight']; // Exclude both the portfolio and weight column in PDF if in compare mode
	private static readonly excludedSinglePortfolioColumns = ['portfolio']; // Exclude only the portfolio column in PDF for single portfolio view, just like in web
	private static readonly cellDefaults: Partial<ColumnComposition> = {
		textClasses: ['text-8'],
		verticalAlignment: Alignment.Vertical.Middle,
		boxModelConstraint: { paddingTop: 5, paddingBottom: 5, paddingLeft: 10 }
	};

	public static buildTable(
		topHoldings: TabData,
		bottomHoldings: TabData,
		contentService: ContentService,
		columnProportions?: number[],
		isCarbonScoreData?: boolean
	): AbstractRenderer<SVGGElement>[] {
		const topTableData: TableData = topHoldings?.tableData?.[0];
		const bottomTableData: TableData = bottomHoldings?.tableData?.[0];
		const isSinglePortfolio: boolean = topTableData?.portfolioData?.length === 1;
		if (!columnProportions || !columnProportions.length) {
			columnProportions = isSinglePortfolio ? [10, 10, 37, 10, 13, 10, 10] : [10, 10, 47, 13, 10, 10];
		}

		const tableDefinition = this.getColumnCompositions(topTableData, bottomTableData, columnProportions, isSinglePortfolio, contentService, isCarbonScoreData);

		const headerRow: RowPanel = new RowPanel(
			`${this.name} Table Header Row`,
			tableDefinition.headers.map((config: ColumnComposition) => config.renderer.render()),
			{ marginBottom: 5, paddingTop: 5, borderBottom: 0.25, borderBottomColor: '#000' },
			Alignment.Horizontal.Left,
			Alignment.Vertical.Bottom
		);
		const rows: Array<RowPanel> = tableDefinition.body.map((configs: Array<ColumnComposition>) =>
			new RowPanel(
				'bodyRow',
				configs.map((config: ColumnComposition) => config.renderer.render()),
				{ borderBottom: 0.25, borderBottomColor: '#000' },
				Alignment.Horizontal.Left,
				Alignment.Vertical.Middle
			)
		);

		return [headerRow, ...rows];
	}

	private static getColumnCompositions(
		topTableData: TableData,
		bottomTableData: TableData,
		columnProportions: Array<number>,
		isSinglePortfolio: boolean,
		contentService: ContentService,
		isCarbonScoreData?: boolean
	): ColumnCompositions {
		const categoryHeader = this.getCategoryCell('ESG_HOLDING_RANKINGS_CATEGORY', '', contentService);
		const tableHeaders: Array<CellData> = CellUtil.filterRow([categoryHeader, topTableData.firstColumnTitle, ...topTableData.headersX])
			.filter((cell: CellData) => this.isNotExcludedColumn(cell, isSinglePortfolio));

		return new ColumnCompositions(
			this.getHeaderRowCompositions(tableHeaders, columnProportions),
			this.getBodyRowCompositions(topTableData, bottomTableData, columnProportions, isSinglePortfolio, contentService, isCarbonScoreData)
		);
	}

	private static getHeaderRowCompositions(headers: Array<CellData>, columnProportions: Array<number>): Array<ColumnComposition> {
		return headers.map((header: CellData, index: number) =>
			new ColumnComposition({
				cells: [header],
				widthProportion: columnProportions[index],
				textClasses: ['text-bold', 'text-8'],
				horizontalAlignment: this.getHorizontalAlignment(header),
				verticalAlignment: Alignment.Vertical.Bottom,
				boxModelConstraint: !!index ? { paddingLeft: 10 } : {}
			})
		);
	}

	private static getBodyRowCompositions(
		topTableData: TableData,
		bottomTableData: TableData,
		columnProportions: number[],
		isSinglePortfolio: boolean,
		contentService: ContentService,
		isCarbonScoreData?: boolean
	): ColumnComposition[][] {
		const topCategoryCell = this.getCategoryCell(
			isCarbonScoreData ? 'ESG_CARBON_TOP_HOLDING_RANKINGS_CATEGORY' : 'ESG_TOP_HOLDING_RANKINGS_CATEGORY',
			'Top ESG Holdings',
			contentService
		);
		const bottomCategoryCell = this.getCategoryCell(
			isCarbonScoreData ? 'ESG_CARBON_BOTTOM_HOLDING_RANKINGS_CATEGORY' : 'ESG_BOTTOM_HOLDING_RANKINGS_CATEGORY',
			'Bottom ESG Holdings',
			contentService
		);
		return [
			this.getMergedRow(topTableData, topCategoryCell, columnProportions, isSinglePortfolio),
			this.getMergedRow(bottomTableData, bottomCategoryCell, columnProportions, isSinglePortfolio)
		];
	}

	private static getMergedRow(tableData: TableData, categoryCell: CellData, columnProportions: number[], isSinglePortfolio: boolean): ColumnComposition[] {
		const tickers: Array<CellData> = CellUtil.filterRow(tableData.headersY);
		const holdingsTableRows = tableData.cells.filter((row: Array<CellData>) => CellUtil.isCellVisible(row[0]));
		const firstColCell = { ...tickers[0], formattedValue: tickers.map(cell => cell.formattedValue).join(','), rawValue: tickers.map(cell => cell.rawValue) };
		const mergedHoldingsRow = holdingsTableRows[0].map((cell, index) => {
			const column = holdingsTableRows.map(row => row[index]);
			return {
				...cell,
				rawValue: column.map(cell => cell.rawValue),
				formattedValue: column.map(cell => cell.formattedValue).join(',')
			};
		});

		const getCell = (cell: CellData, index: number, renderer?: CellRenderer) => new ColumnComposition({
			...this.cellDefaults,
			boxModelConstraint: { ...this.cellDefaults.boxModelConstraint, paddingLeft: 0 },
			cells: [cell],
			widthProportion: columnProportions[index],
			horizontalAlignment: this.getHorizontalAlignment(cell),
			renderer
		});

		const categoryCol = getCell(categoryCell, 0);
		const firstCol = getCell(firstColCell, 1, this.getMultiLineRenderer(firstColCell, 1, this.cellDefaults, columnProportions));
		const otherCols = CellUtil.filterRow(mergedHoldingsRow)
			.filter(cell => this.isNotExcludedColumn(cell, isSinglePortfolio))
			.map((cell: CellData, cellIndex: number) => getCell(cell, cellIndex + 2, this.getMultiLineRenderer(cell, cellIndex + 2, this.cellDefaults, columnProportions)));

		return [categoryCol, firstCol, ...otherCols];
	}

	private static getCategoryCell(key: string, defaultValue: string, contentService: ContentService): CellData {
		const attribute = { align: 'text-align-left' };
		return CellData.create({
			config: new ColumnConfig(new ColumnText(key, defaultValue, null, attribute), null, new StaticValueExtractor(defaultValue)),
			rawValue: defaultValue,
			formattedValue: contentService.get(key, defaultValue),
			attribute
		});
	}

	private static getHorizontalAlignment(cell: CellData): Alignment.Horizontal {
		return cell.config?.label?.attributes?.align?.includes('left') ? Alignment.Horizontal.Left : Alignment.Horizontal.Right;
	}

	private static isNotExcludedColumn(cell: CellData, isSinglePortfolio: boolean): boolean {
		const isNotSinglePortfolioExcludedColumn = !(isSinglePortfolio && this.excludedSinglePortfolioColumns.includes(cell?.config?.id));
		const isNotMultiPortfolioExcludedColumn = !(!isSinglePortfolio && this.excludedMultiPortfolioColumns.includes(cell?.config?.id));
		return isNotSinglePortfolioExcludedColumn && isNotMultiPortfolioExcludedColumn;
	}

	private static getMultiLineRenderer(cell: CellData, cellIndex: number, cellDefaults: Partial<ColumnComposition>, columnProportions: number[]): MultipleLineTextRenderer {
		return new MultipleLineTextRenderer(
			cell.formattedValue
				.split(',')
				.map((item: string) => ({
					cell: item,
					textClasses: cellDefaults.textClasses,
					constraints: { paddingTop: 5, paddingBottom: 5, paddingLeft: 10 }
				})),
			this.getHorizontalAlignment(cell),
			cellDefaults.verticalAlignment,
			columnProportions[cellIndex],
			cellDefaults.boxModelConstraint
		);
	}
}
